import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Check, Download, Loader2, RotateCw, Search } from 'lucide-react';
import { toast } from 'sonner';
import { Switch } from '@/components/ui/switch';
import {
  notificationSettingsService,
  type ActivityPage,
  type ActivityScope,
  type ActivitySource,
  type DeliveryStatus,
  type SendAttempt,
  type StoredEmail
} from '@/services/notificationSettingsService';
import {
  categoryForNotificationType,
  getMyEmailNotificationCategory,
  type MyEmailNotificationCategory
} from '../../../../supabase/functions/_shared/my-email-notifications-catalog';
import { canResendFromActivity } from './config';
import { StoredEmailViewer } from './StoredEmailViewer';

interface ActivityTabProps {
  /** Admin scope: the one notification type this tab lists. */
  notificationType?: string;
  /** 'admin' (default) = Company Settings tab; 'mine' = Tools → My Email Notifications. */
  scope?: ActivityScope;
  /** 'mine' scope: the catalog category whose types are listed. */
  categoryId?: string;
}

const DEFAULT_CONFIRM_COPY = 'Sends the current email to the same address again. The loan officer stays copied.';
/** Re-evaluate server-reported cooldowns once a minute so a button re-enables when its time arrives. */
const ELIGIBILITY_TICK_MS = 60 * 1000;

const PAGE_SIZE = 50;

const DATE_RANGE_OPTIONS = [
  { value: '1', label: 'Last 24h' },
  { value: '7', label: 'Last 7 days' },
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days' },
  { value: '365', label: 'Last 12 months' },
  { value: 'all', label: 'All time' }
];

const ROLE_OPTIONS = [
  { value: 'all', label: 'Any role' },
  { value: 'borrower', label: 'Borrower' },
  { value: 'co_borrower', label: 'Co-borrower' },
  { value: 'loan_officer', label: 'Loan Officer' },
  { value: 'loan_officer_2', label: 'LO2' },
  { value: 'loan_officer_assistant', label: 'LOA' },
  { value: 'processor', label: 'Processor' },
  { value: 'closer', label: 'Closer' },
  { value: 'realtor', label: 'Realtor' }
];

/** Only the roles the review-request templates know can be rebuilt for a resend. */
const RESENDABLE_ROLES = new Set(['borrower', 'co_borrower', 'realtor']);

type PillTone = 'success' | 'danger' | 'warning' | 'neutral';

/**
 * Status pill. Provider-confirmed delivery wins; without it the row status stands, where
 * "Sent" means the provider accepted the email, not that it reached the inbox.
 */
function formatDelivery(delivery: DeliveryStatus | null | undefined): { label: string; title: string; tone: PillTone } | null {
  switch (delivery?.state) {
    case 'delivered':
      return { label: 'Delivered', title: `Delivered${delivery.at ? ` ${formatTimestamp(delivery.at)}` : ''}`, tone: 'success' };
    case 'bounced':
      return { label: 'Bounced', title: `Bounced${delivery.at ? ` ${formatTimestamp(delivery.at)}` : ''}`, tone: 'warning' };
    case 'complained':
      return { label: 'Spam', title: 'Marked as spam by the recipient', tone: 'danger' };
    case 'pending':
      return { label: 'Sent', title: 'Sent, awaiting delivery confirmation', tone: 'neutral' };
    case 'unknown':
      return { label: 'Sent', title: 'Sent, no delivery record', tone: 'neutral' };
    default:
      return null;
  }
}

function formatStatus(status: string | null): { label: string; tone: PillTone } {
  const normalized = (status || 'sent').toLowerCase();
  if (normalized === 'sent') {
    return { label: 'Sent', tone: 'success' };
  }
  if (normalized === 'delivered') {
    return { label: 'Delivered', tone: 'success' };
  }
  if (normalized === 'failed' || normalized === 'complained') {
    return {
      label: 'Failed',
      tone: 'danger'
    };
  }
  if (normalized === 'bounced') {
    return { label: 'Bounced', tone: 'warning' };
  }
  return { label: normalized.charAt(0).toUpperCase() + normalized.slice(1), tone: 'neutral' };
}

function recipientSummary(attempt: SendAttempt): string {
  const to = attempt.toEmails.join(', ') || 'Unknown';
  return attempt.ccEmails.length > 0 ? `${to}; CC ${attempt.ccEmails.join(', ')}` : to;
}

function formatTimestamp(value: string | null): string {
  if (!value) {
    return 'Unknown';
  }
  return new Date(value).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
}

function roleKey(value: string | null | undefined): string {
  const normalized = (value || '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
  if (normalized === 'lo2') {
    return 'loan_officer_2';
  }
  if (normalized === 'loa') {
    return 'loan_officer_assistant';
  }
  return normalized;
}

function roleLabel(value: string | null | undefined): string {
  const normalized = roleKey(value);
  const match = ROLE_OPTIONS.find((option) => option.value === normalized);
  if (match && match.value !== 'all') {
    return match.label;
  }
  return value || '-';
}

function loanLabel(attempt: SendAttempt): string {
  if (attempt.context.nmls_number) {
return `NMLS ${attempt.context.nmls_number}`;
}
  return attempt.context.loan_number || attempt.context.encompass_loan_id || '-';
}

/**
 * A row can be resent when its notification type supports it and the row is a real send
 * to a recipient the review templates can rebuild. The server enforces the same rules.
 */
function canResendAttempt(attempt: SendAttempt): boolean {
  if (attempt.notificationType === 'recruit_sign_in_verification') {
return !attempt.context.is_test && !!attempt.context.recruit_id;
}
  return canResendFromActivity(attempt.notificationType)
    && !attempt.context.is_test
    && attempt.toEmails.length > 0
    && Boolean(attempt.context.encompass_loan_id)
    && RESENDABLE_ROLES.has(roleKey(attempt.context.recipient_role));
}

type RowResendState =
  | { kind: 'button' }
  | { kind: 'hidden' }
  | { kind: 'limit_reached' }
  | { kind: 'cooldown'; availableAt: number };

/**
 * What the Resend cell shows for a row. Admin scope: always the button (the server has no
 * limits for admins). Self-service scope: exactly what the server reported from the ledger;
 * a cooldown whose time has passed becomes the button again.
 */
function rowResendState(attempt: SendAttempt, isMine: boolean, now: number): RowResendState {
  if (!isMine) {
    return { kind: 'button' };
  }
  const eligibility = attempt.resend;
  if (!eligibility || eligibility.allowed) {
    return { kind: 'button' };
  }
  if (eligibility.reason === 'not_recorded') {
    return { kind: 'hidden' };
  }
  if (eligibility.reason === 'limit_reached') {
    return { kind: 'limit_reached' };
  }
  const availableAt = Date.parse(eligibility.availableAt);
  return Number.isNaN(availableAt) || availableAt <= now ? { kind: 'button' } : { kind: 'cooldown', availableAt };
}

function formatAvailableAt(value: number): string {
  const date = new Date(value);
  const sameDay = date.toDateString() === new Date().toDateString();
  const time = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  return sameDay ? `Available ${time}` : `Available ${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} ${time}`;
}

export function ActivityTab({ notificationType = '', scope = 'admin', categoryId }: ActivityTabProps) {
  const isMine = scope === 'mine';
  const source = useMemo<ActivitySource>(() => (isMine ? { scope: 'mine', categoryId } : { scope: 'admin' }), [isMine, categoryId]);
  const category: MyEmailNotificationCategory | null = isMine
    ? getMyEmailNotificationCategory(categoryId)
    : categoryForNotificationType(notificationType);
  const isRecruitAuth = notificationType === 'recruit_sign_in_verification';
  const confirmCopy = isRecruitAuth ? 'Requests a fresh code at the current saved email and copies the assigned recruiter and configured staff. The previous code stops working. The same cooldown and hourly limits apply.' : category?.copy.confirm ?? DEFAULT_CONFIRM_COPY;
  const [includeTests, setIncludeTests] = useState(false);
  const [activity, setActivity] = useState<ActivityPage | null>(null);
  const [query, setQuery] = useState('');
  const [dateRange, setDateRange] = useState('30');
  const [role, setRole] = useState('all');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [storedEmail, setStoredEmail] = useState<StoredEmail | null>(null);
  // Manual resend (Google review requests only). One confirm strip is open at a time; rows
  // resent during this visit keep a "Resent" chip so the same row is not sent twice by accident.
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const [resendingId, setResendingId] = useState<string | null>(null);
  // Keyed by row so a late failure can never land under a different row's strip.
  const [resendError, setResendError] = useState<{ id: string; message: string } | null>(null);
  const [resentIds, setResentIds] = useState<Set<string>>(() => new Set());
  // Bumped after a resend so the new row appears without blanking the table.
  const [refreshTick, setRefreshTick] = useState(0);
  const silentRefreshRef = useRef(false);
  const cancelButtonRef = useRef<HTMLButtonElement | null>(null);

  const resendEnabled = isMine ? Boolean(category?.resendKind) : canResendFromActivity(notificationType);

  useEffect(() => {
    setConfirmingId(null);
    setResendError(null);
    setResentIds(new Set());
  }, [notificationType, categoryId, scope]);

  useEffect(() => {
    let cancelled = false;
    const silent = silentRefreshRef.current;
    silentRefreshRef.current = false;
    const load = async () => {
      if (!silent) {
        setLoading(true);
      }
      setError(null);
      try {
        const nextActivity = await notificationSettingsService.getActivityPage(notificationType, {
          ...source,
          page,
          pageSize: PAGE_SIZE,
          q: query,
          dateRange,
          role,
          includeTests: isMine ? false : includeTests
        });
        if (!cancelled) {
          setActivity(nextActivity);
          if (nextActivity.page !== page) {
            setPage(nextActivity.page);
          }
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Unable to load notification Activity');
          setActivity(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [notificationType, source, isMine, page, query, dateRange, role, includeTests, refreshTick]);

  // Radix convention for confirmations: land on the least destructive control.
  useEffect(() => {
    if (confirmingId) {
      cancelButtonRef.current?.focus();
    }
  }, [confirmingId]);

  // Self-service cooldowns are reported as absolute times; tick so they lapse on screen.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!isMine) {
      return undefined;
    }
    const timer = window.setInterval(() => setNow(Date.now()), ELIGIBILITY_TICK_MS);
    return () => window.clearInterval(timer);
  }, [isMine]);

  const openSentEmail = async (attempt: SendAttempt) => {
    if (!attempt.hasStoredHtml) {
      return;
    }
    try {
      const email = await notificationSettingsService.getStoredEmail(attempt.id, notificationType, source);
      setStoredEmail(email);
      setViewerOpen(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sent email is not available for this row');
    }
  };

  const resetPage = () => setPage(1);

  const exportCsv = async () => {
    setExporting(true);
    setError(null);
    try {
      const result = await notificationSettingsService.exportActivityCsv(notificationType, {
        ...source,
        q: query,
        dateRange,
        role,
        includeTests: isMine ? false : includeTests
      });
      const blob = new Blob([result.csv], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = result.filename;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to export notification Activity');
    } finally {
      setExporting(false);
    }
  };

  const toggleConfirm = (attempt: SendAttempt) => {
    if (resendingId) {
      return;
    }
    setResendError(null);
    setConfirmingId((current) => (current === attempt.id ? null : attempt.id));
  };

  const closeConfirm = () => {
    if (resendingId) {
      return;
    }
    setConfirmingId(null);
    setResendError(null);
  };

  const confirmResend = async (attempt: SendAttempt) => {
    setResendingId(attempt.id);
    setResendError(null);
    try {
      const result = isRecruitAuth ? await notificationSettingsService.resendRecruitCode(attempt.id) : await notificationSettingsService.resendReviewRequest(attempt.id, source);
      if (!result.ok) {
        const message = result.code === 'cooldown' && result.retryAfter
          ? `${result.message} ${formatAvailableAt(Date.parse(result.retryAfter))}.`
          : result.message;
        setResendError({ id: attempt.id, message });
        return;
      }
      setResentIds((current) => {
        const next = new Set(current);
        next.add(attempt.id);
        return next;
      });
      setConfirmingId(null);
      const recipient = result.recipient || attempt.toEmails[0];
      if (result.warnings.length > 0) {
        // The email left; our own log did not fully update. Say exactly that.
        toast.warning(`Resent to ${recipient}, but the activity log could not be fully updated.`);
      } else {
        toast.success(isRecruitAuth ? 'Fresh code requested. Check Activity for delivery.' : `Resent to ${recipient}`);
      }
      silentRefreshRef.current = true;
      setRefreshTick((tick) => tick + 1);
    } finally {
      setResendingId(null);
    }
  };

  const rows = useMemo(() => activity?.rows ?? [], [activity]);
  const hasNarrowingFilters = query.trim().length > 0 || role !== 'all';
  const stats = activity?.stats || { total: 0, delivered: 0, opened: 0, bounced: 0, failed: 0, openRate: null };
  const total = activity?.total || 0;
  const totalPages = activity?.totalPages || 1;
  const showingStart = total === 0 ? 0 : ((activity?.page || page) - 1) * PAGE_SIZE + 1;
  const showingEnd = Math.min((activity?.page || page) * PAGE_SIZE, total);

  return (
    <div className="en-section en-activity-log">
      <div className="en-section-title">
        <div>
          <span>Recent activity</span>
        </div>
        {!isMine && (
          <label className="flex items-center gap-2 text-xs text-muted-foreground">
            Show tests
            <Switch
              checked={includeTests}
              onCheckedChange={(checked) => {
                setIncludeTests(checked);
                resetPage();
              }}
            />
          </label>
        )}
      </div>
      {error && <div className="en-warning mb-3">{error}</div>}

      <div className="en-activity-stats">
        <div className="en-stat-card">
          <div className="en-stat-label">{isRecruitAuth ? 'Requests' : 'Total sent'}</div>
          <div className="en-stat-value">{stats.total.toLocaleString()}</div>
          <div className="en-stat-sub">in current view</div>
        </div>
        <div className="en-stat-card">
          <div className="en-stat-label">Delivered</div>
          <div className="en-stat-value success">{stats.delivered.toLocaleString()}</div>
          <div className="en-stat-sub">confirmed by the provider</div>
        </div>
        <div className="en-stat-card">
          <div className="en-stat-label">Bounced</div>
          <div className={`en-stat-value${stats.bounced > 0 ? ' danger' : ''}`}>{stats.bounced.toLocaleString()}</div>
          <div className="en-stat-sub">address rejected</div>
        </div>
      </div>

      {isRecruitAuth && <div className="mb-4 text-sm text-muted-foreground" role="status">
        Email code verified: {stats.verified ?? 0}. Recruit opens: {stats.opened}. Open rate: {(stats.openRate === null || stats.openRate === undefined) ? 'Unavailable' : `${stats.openRate}%`}.
        Opens and clicks are observations; they do not establish who entered a code. Codes are hidden in stored emails and exports.
      </div>}
      <div className="en-activity-filters">
        <div className="en-search">
          <Search />
          <input
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              resetPage();
            }}
            placeholder={isRecruitAuth ? "Search by email, NMLS, or verification status..." : "Search by email or loan number..."}
          />
        </div>
        <select
          className="en-activity-select"
          value={dateRange}
          onChange={(event) => {
            setDateRange(event.target.value);
            resetPage();
          }}
        >
          {DATE_RANGE_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
        </select>
        <select
          className="en-activity-select"
          disabled={isRecruitAuth}
          value={role}
          onChange={(event) => {
            setRole(event.target.value);
            resetPage();
          }}
        >
          {ROLE_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
        </select>
        <button type="button" className="en-btn" onClick={exportCsv} disabled={exporting || total === 0}>
          {exporting ? <Loader2 className="animate-spin" /> : <Download />}
          Export CSV
        </button>
      </div>

      <div className={`en-activity-table${resendEnabled ? ' has-actions' : ''}`}>
        <div className="en-activity-head">
          <span>Timestamp</span>
          <span>Recipient</span>
          <span>{isRecruitAuth ? 'NMLS' : 'Loan'}</span>
          <span>Role</span>
          <span>Status</span>
          {resendEnabled && <span className="sr-only">Actions</span>}
        </div>
        {loading ? (
          <div className="flex h-24 items-center justify-center">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          </div>
        ) : rows.length === 0 ? (
          <div className="en-activity-empty">
            {isMine && !hasNarrowingFilters && category ? (
              <>
                <div>{category.copy.empty}</div>
                {dateRange !== 'all' && <span>Try a wider date range.</span>}
              </>
            ) : (
              <>
                <div>No matching activity</div>
                <span>Try widening the date range or clearing filters.</span>
              </>
            )}
          </div>
        ) : rows.map((attempt) => {
          const delivery = formatDelivery(attempt.delivery);
          const status = delivery ?? { ...formatStatus(attempt.status), title: 'Accepted by the email provider' };
          const recipients = recipientSummary(attempt);
          const resendable = canResendAttempt(attempt);
          const resendState = resendable ? rowResendState(attempt, isMine, now) : { kind: 'hidden' as const };
          const confirming = confirmingId === attempt.id;
          const sending = resendingId === attempt.id;
          const anotherSending = resendingId !== null && !sending;
          const rowError = resendError?.id === attempt.id ? resendError.message : null;
          const resent = resentIds.has(attempt.id);
          const primaryEmail = attempt.toEmails[0] || 'this recipient';
          const confirmId = `resend-confirm-${attempt.id}`;
          const itemClass = [
            'en-activity-item',
            attempt.hasStoredHtml ? 'is-openable' : '',
            confirming ? 'is-confirming' : ''
          ].filter(Boolean).join(' ');
          return (
            <div key={attempt.id} className={itemClass}>
              <div className="en-activity-row">
                <button
                  type="button"
                  className={`en-activity-main ${attempt.hasStoredHtml ? 'is-clickable' : 'is-static'}`}
                  disabled={!attempt.hasStoredHtml}
                  title={attempt.hasStoredHtml ? 'Open sent email' : 'Sent email is not available for this row'}
                  onClick={() => openSentEmail(attempt)}
                >
                  <span className="en-activity-time">
                    {attempt.context.resend_of && (
                      <span className="en-activity-resent-mark" title="Manual resend of an earlier email">
                        <RotateCw aria-hidden="true" />
                        <span className="sr-only">Manual resend</span>
                      </span>
                    )}
                    {formatTimestamp(attempt.sentAt)}
                  </span>
                  <span className="en-activity-recipients mono" title={recipients}>
                    {recipients}
                  </span>
                  <span className="en-activity-loan mono" title={loanLabel(attempt)}>{loanLabel(attempt)}</span>
                  <span className="en-activity-role" title={roleLabel(attempt.context.recipient_role)}>{roleLabel(attempt.context.recipient_role)}</span>
                  <span className={`en-status-pill is-${status.tone}`} title={status.title}>{status.label}</span>
                </button>
                {resendEnabled && (
                  <div className="en-activity-actions">
                    {resendState.kind !== 'hidden' && (resent ? (
                      <span className="en-resent-chip">
                        <Check aria-hidden="true" />
                        Resent
                      </span>
                    ) : resendState.kind === 'limit_reached' ? (
                      <button type="button" className="en-row-btn" disabled title="This recipient has reached the limit of send attempts.">
                        Limit reached
                      </button>
                    ) : resendState.kind === 'cooldown' ? (
                      <button type="button" className="en-row-btn" disabled title="Emailed less than 24 hours ago.">
                        {formatAvailableAt(resendState.availableAt)}
                      </button>
                    ) : (
                      <button
                        type="button"
                        className={`en-row-btn${confirming ? ' is-active' : ''}`}
                        aria-expanded={confirming}
                        aria-controls={confirming ? confirmId : undefined}
                        disabled={sending || anotherSending}
                        onClick={() => toggleConfirm(attempt)}
                      >
                        <RotateCw aria-hidden="true" />
                        Resend
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {attempt.auth && <details className="px-4 py-3 text-sm border-t border-slate-100">
                <summary className="cursor-pointer font-medium">{attempt.auth.verificationStatus === 'verified' ? 'Email code verified' : `Code ${attempt.auth.verificationStatus}`} · Delivery and engagement</summary>
                {attempt.auth.verifiedAt && <p className="mt-2">Email code verified {formatTimestamp(attempt.auth.verifiedAt)}</p>}
                <ul className="mt-2 space-y-3">{attempt.auth.recipients.map(recipient => <li key={recipient.email}>
                  <strong>{recipient.role}: {recipient.email}</strong> — {recipient.state}
                  <p>Opened: {recipient.openedAt ? formatTimestamp(recipient.openedAt) : attempt.auth?.trackingAvailable ? 'Not observed' : 'Unavailable'} · Clicked: {recipient.clickedAt ? formatTimestamp(recipient.clickedAt) : 'Not observed'}</p>
                  {recipient.events.map((event,index) => <p key={`${event.at}-${index}`} className="text-xs text-muted-foreground">{event.type.replace('email.','')} · {formatTimestamp(event.at)}</p>)}
                </li>)}</ul>
              </details>}
              {confirming && (
                <div
                  id={confirmId}
                  className="en-activity-confirm"
                  role="group"
                  aria-label={`Resend to ${primaryEmail}`}
                  onKeyDown={(event) => {
                    if (event.key === 'Escape') {
                      event.stopPropagation();
                      closeConfirm();
                    }
                  }}
                >
                  <div className="en-activity-confirm-copy">
                    <strong>Resend to {primaryEmail}?</strong>
                    <span>{confirmCopy}</span>
                    {rowError && (
                      <span className="en-activity-confirm-error" role="alert">Resend failed: {rowError}</span>
                    )}
                  </div>
                  <div className="en-activity-confirm-actions">
                    <button ref={cancelButtonRef} type="button" className="en-btn" onClick={closeConfirm} disabled={sending}>
                      Cancel
                    </button>
                    <button type="button" className="en-btn en-btn-primary" onClick={() => void confirmResend(attempt)} disabled={sending}>
                      {sending ? <Loader2 className="animate-spin" aria-hidden="true" /> : <RotateCw aria-hidden="true" />}
                      {sending ? 'Sending…' : 'Resend'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {total > 0 && (
        <div className="en-activity-pagination">
          <div>
            Showing <strong>{showingStart.toLocaleString()}-{showingEnd.toLocaleString()}</strong> of <strong>{total.toLocaleString()}</strong>
          </div>
          <div className="en-activity-page-controls">
            <button type="button" className="en-btn" disabled={page <= 1 || loading} onClick={() => setPage((current) => Math.max(1, current - 1))}>
              Previous
            </button>
            <label>
              Page
              <input
                type="number"
                min={1}
                max={totalPages}
                value={page}
                onChange={(event) => {
                  const next = Number.parseInt(event.target.value, 10);
                  if (!Number.isNaN(next)) {
                    setPage(Math.min(totalPages, Math.max(1, next)));
                  }
                }}
              />
              of {totalPages.toLocaleString()}
            </label>
            <button type="button" className="en-btn" disabled={page >= totalPages || loading} onClick={() => setPage((current) => Math.min(totalPages, current + 1))}>
              Next
            </button>
          </div>
        </div>
      )}
      <StoredEmailViewer email={storedEmail} open={viewerOpen} onOpenChange={setViewerOpen} />
    </div>
  );
}
